import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import PlanSearchSelect from '../../components/admin/PlanSearchSelect';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Layers, Plus, Pencil, Trash2, ArrowUp, ArrowDown, Loader2, Save, X, Video, Copy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OBJECTIVES = [
  { value: 'senior', label: 'Senior' },
  { value: 'atletico', label: 'Atlético Funcional' },
  { value: 'salud', label: 'Salud General' },
  { value: 'recomp', label: 'Recomposición' },
  { value: 'hipertrofia', label: 'Hipertrofia' },
];

const EMPTY_FORM = { name: '', objective: '', description: '', slots: [null, null, null, null, null] };

export default function MacrosPage() {
  const [macros, setMacros] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [gapsTarget, setGapsTarget] = useState(null);
  const [gapsData, setGapsData] = useState(null);
  const [gapsLoading, setGapsLoading] = useState(false);

  const openVideoGaps = async (macro) => {
    setGapsTarget(macro);
    setGapsData(null);
    setGapsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/video-gaps`, { params: { macro_id: macro.id } });
      setGapsData(res.data);
    } catch (e) {
      toast.error('Error generando la lista de rodaje');
      setGapsTarget(null);
    } finally {
      setGapsLoading(false);
    }
  };

  const copyGapsList = () => {
    if (!gapsData) return;
    const lines = [
      `LISTA DE RODAJE · ${gapsData.name}`,
      `${gapsData.without_video} ejercicios sin vídeo (de ${gapsData.total_distinct} distintos):`,
      '',
      ...gapsData.gaps.map((g) => `- ${g.exercise_id || '??'} · ${g.name} (usado ${g.uses}x) → archivo: ${g.expected_filename}`),
    ];
    navigator.clipboard.writeText(lines.join('\n'));
    toast.success('Lista copiada al portapapeles 📋');
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [mRes, pRes] = await Promise.all([
        axios.get(`${API}/admin/macros`),
        axios.get(`${API}/plans`),
      ]);
      setMacros(mRes.data?.macros || []);
      setPlans(pRes.data || []);
    } catch (e) {
      toast.error('Error cargando macros o planes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const planName = (id) => plans.find((p) => p.id === id)?.name || id;

  const openNew = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowBuilder(true);
  };

  const openEdit = (macro) => {
    setEditingId(macro.id);
    setForm({
      name: macro.name || '',
      objective: macro.objective || '',
      description: macro.description || '',
      slots: [0, 1, 2, 3, 4].map((i) => macro.mesocycles?.[i]?.plan_id || null),
    });
    setShowBuilder(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const setSlot = (idx, planId) => {
    setForm((f) => {
      const slots = [...f.slots];
      slots[idx] = planId;
      return { ...f, slots };
    });
  };

  const moveSlot = (idx, dir) => {
    setForm((f) => {
      const slots = [...f.slots];
      const j = idx + dir;
      if (j < 0 || j > 4) return f;
      [slots[idx], slots[j]] = [slots[j], slots[idx]];
      return { ...f, slots };
    });
  };

  const filledSlots = form.slots.filter(Boolean).length;
  const isValid = form.name.trim().length >= 3 && form.objective && filledSlots === 5;

  const handleSave = async () => {
    if (!isValid) return;
    setSaving(true);
    try {
      if (editingId) {
        const mesocycles = form.slots.map((pid, i) => ({
          order: i + 1,
          plan_id: pid,
          plan_name: planName(pid),
        }));
        await axios.put(`${API}/admin/macros/${editingId}`, {
          name: form.name.trim(),
          objective: form.objective,
          description: form.description,
          mesocycles,
        });
        toast.success('Macro actualizado');
      } else {
        await axios.post(`${API}/admin/macros`, {
          name: form.name.trim(),
          objective: form.objective,
          description: form.description,
          mesocycle_plan_ids: form.slots,
        });
        toast.success('Macro creado 🎉');
      }
      setShowBuilder(false);
      setForm(EMPTY_FORM);
      setEditingId(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error guardando el macro');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/admin/macros/${deleteTarget.id}`);
      toast.success('Macro borrado');
      setDeleteTarget(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error borrando');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="macros-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Layers className="h-6 w-6 text-primary" /> Macros
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Encadena 5 planes (1 por mesociclo) en un programa automático de 25 semanas
            </p>
          </div>
          {!showBuilder && (
            <Button onClick={openNew} className="bg-primary text-primary-foreground font-bold" data-testid="new-macro-btn">
              <Plus className="h-4 w-4 mr-2" /> Nuevo Macro
            </Button>
          )}
        </div>

        {/* ===== BUILDER ===== */}
        {showBuilder && (
          <Card className="border-primary/40" data-testid="macro-builder">
            <CardContent className="p-5 space-y-5">
              <div className="flex items-center justify-between">
                <h2 className="font-bold text-lg">{editingId ? 'Editar Macro' : 'Nuevo Macro'}</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowBuilder(false)} data-testid="macro-builder-close">
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre del macro</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Ej: SENIOR · Macro 1"
                    data-testid="macro-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Objetivo (para auto-asignación)</Label>
                  <Select value={form.objective} onValueChange={(v) => setForm((f) => ({ ...f, objective: v }))}>
                    <SelectTrigger data-testid="macro-objective-select">
                      <SelectValue placeholder="Selecciona objetivo" />
                    </SelectTrigger>
                    <SelectContent>
                      {OBJECTIVES.map((o) => (
                        <SelectItem key={o.value} value={o.value} data-testid={`macro-objective-${o.value}`}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Descripción (opcional)</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Notas internas sobre este macro..."
                  rows={2}
                  data-testid="macro-description-input"
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Mesociclos (5 planes en orden)</Label>
                  <span className={`text-xs font-bold ${filledSlots === 5 ? 'text-emerald-400' : 'text-amber-400'}`} data-testid="macro-slots-counter">
                    {filledSlots}/5 completados
                  </span>
                </div>
                {form.slots.map((planId, idx) => (
                  <div key={idx} className="flex items-center gap-2" data-testid={`macro-slot-${idx + 1}`}>
                    <span className="flex-shrink-0 w-20 text-sm font-bold text-primary">Meso {idx + 1}</span>
                    <div className="flex-1 min-w-0">
                      <PlanSearchSelect
                        plans={plans}
                        value={planId}
                        onChange={(id) => setSlot(idx, id)}
                        placeholder={`Plan del mesociclo ${idx + 1}...`}
                        testId={`macro-slot-select-${idx + 1}`}
                      />
                    </div>
                    <div className="flex flex-col">
                      <button
                        type="button"
                        onClick={() => moveSlot(idx, -1)}
                        disabled={idx === 0}
                        className="text-muted-foreground hover:text-foreground disabled:opacity-20 p-0.5"
                        aria-label="Subir"
                        data-testid={`macro-slot-up-${idx + 1}`}
                      >
                        <ArrowUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveSlot(idx, 1)}
                        disabled={idx === 4}
                        className="text-muted-foreground hover:text-foreground disabled:opacity-20 p-0.5"
                        aria-label="Bajar"
                        data-testid={`macro-slot-down-${idx + 1}`}
                      >
                        <ArrowDown className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-3 pt-2 border-t border-border">
                <Button variant="outline" onClick={() => setShowBuilder(false)}>Cancelar</Button>
                <Button
                  onClick={handleSave}
                  disabled={!isValid || saving}
                  className="bg-primary text-primary-foreground font-bold"
                  data-testid="macro-save-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  {editingId ? 'Guardar cambios' : 'Crear Macro'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ===== LISTA ===== */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : macros.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground" data-testid="macros-empty">
              <Layers className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p className="font-medium">Aún no hay macros</p>
              <p className="text-sm mt-1">Crea el primero: elige 5 planes y el sistema hará el resto.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {macros.map((m) => (
              <Card key={m.id} data-testid={`macro-card-${m.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-foreground truncate">{m.name}</h3>
                        <Badge variant="outline" className="border-primary/50 text-primary text-[10px]">
                          {OBJECTIVES.find((o) => o.value === m.objective)?.label || m.objective}
                        </Badge>
                      </div>
                      {m.description && <p className="text-xs text-muted-foreground mt-1">{m.description}</p>}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => openVideoGaps(m)} title="Lista de rodaje (vídeos pendientes)" data-testid={`macro-videos-${m.id}`}>
                        <Video className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(m)} data-testid={`macro-edit-${m.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(m)} className="text-red-400 hover:text-red-300" data-testid={`macro-delete-${m.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <ol className="mt-4 space-y-1.5">
                    {(m.mesocycles || []).map((meso) => (
                      <li key={meso.order} className="flex items-center gap-2 text-sm">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
                          {meso.order}
                        </span>
                        <span className="text-muted-foreground truncate">{meso.plan_name || planName(meso.plan_id)}</span>
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Lista de rodaje (vídeos pendientes) */}
      <Dialog open={!!gapsTarget} onOpenChange={(o) => !o && setGapsTarget(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="video-gaps-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="h-5 w-5 text-primary" /> Lista de rodaje · {gapsTarget?.name}
            </DialogTitle>
            <DialogDescription>
              Ejercicios distintos de los 5 mesociclos y su estado de vídeo
            </DialogDescription>
          </DialogHeader>
          {gapsLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : gapsData ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border border-border bg-background/50 px-4 py-3">
                <div className="text-sm">
                  <span className="font-bold text-emerald-400">{gapsData.with_video}</span> con vídeo ·{' '}
                  <span className="font-bold text-amber-400">{gapsData.without_video}</span> pendientes ·{' '}
                  <span className="text-muted-foreground">{gapsData.coverage_percent}% cubierto</span>
                </div>
                {gapsData.without_video > 0 && (
                  <Button size="sm" variant="outline" onClick={copyGapsList} data-testid="copy-gaps-btn">
                    <Copy className="h-3.5 w-3.5 mr-1.5" /> Copiar lista
                  </Button>
                )}
              </div>

              {gapsData.without_video === 0 ? (
                <p className="text-center text-emerald-400 py-6 font-medium" data-testid="video-gaps-complete">
                  🎉 Todos los ejercicios de este macro tienen vídeo
                </p>
              ) : (
                <div className="space-y-1.5" data-testid="video-gaps-list">
                  <p className="text-xs font-bold uppercase tracking-wider text-amber-400">Por grabar ({gapsData.without_video})</p>
                  {gapsData.gaps.map((g, i) => (
                    <div key={i} className="flex items-center justify-between gap-3 rounded-md border border-amber-500/20 bg-amber-500/5 px-3 py-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">
                          <span className="text-primary font-mono text-xs mr-2">{g.exercise_id || '— sin GF-ID —'}</span>
                          {g.name}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          Usado {g.uses}x · Archivo esperado: <span className="font-mono">{g.expected_filename}</span>
                          {!g.in_library && <span className="text-red-400 ml-2">⚠ No está en la librería</span>}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {gapsData.with_video > 0 && (
                <details className="text-sm">
                  <summary className="cursor-pointer text-muted-foreground text-xs uppercase tracking-wider font-bold">
                    Ya grabados ({gapsData.with_video})
                  </summary>
                  <div className="mt-2 space-y-1">
                    {gapsData.ready.map((g, i) => (
                      <p key={i} className="text-xs text-muted-foreground px-3">
                        ✅ <span className="font-mono">{g.exercise_id}</span> · {g.name}
                      </p>
                    ))}
                  </div>
                </details>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Confirmación de borrado */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="macro-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Borrar "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Los planes NO se borran, solo el macro. Los clientes que ya lo tengan asignado seguirán con su plan actual pero perderán el avance automático entre mesociclos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-500" data-testid="macro-delete-confirm">
              Borrar macro
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
